import React, { useState, useEffect } from 'react';
import styles from './OrgPointsHistory.module.css';
import { Coins, TrendingUp, TrendingDown, Calendar, User, Award, CheckCircle, Gift } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import axios from '../api/axios';
import { getLevel } from '../utils/levels';

interface PointTransaction {
  id: number;
  points: number;
  reason: string;
  missionId: number;
  createdAt: string;
  createdBy?: string;
  missionTitle?: string;
}

const OrgPointsHistory: React.FC = () => {
  const [orgPoints, setOrgPoints] = useState<number>(0);
  const [transactions, setTransactions] = useState<PointTransaction[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [filter, setFilter] = useState<'all' | 'earned' | 'spent'>('all');
  const { user } = useAuth();

  useEffect(() => {
    const fetchData = async () => {
      if (!user) return;
      
      setLoading(true);
      try {
        // Fetch total points
        const pointsRes = await axios.get('/auth/user/org-points');
        setOrgPoints(pointsRes.data.totalPoints || 0);

        // Fetch transaction history
        const historyRes = await axios.get('/auth/user/org-points/history');
        setTransactions(historyRes.data || []);
      } catch (err) {
        console.error('Failed to fetch ORG points data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user]);

  const filteredTransactions = transactions.filter(t => {
    if (filter === 'earned') return t.points > 0;
    if (filter === 'spent') return t.points < 0;
    return true;
  });

  const totalEarned = transactions.filter(t => t.points > 0).reduce((sum, t) => sum + t.points, 0);
  const totalSpent = Math.abs(transactions.filter(t => t.points < 0).reduce((sum, t) => sum + t.points, 0));

  const { level, nextThreshold, progressPct } = getLevel(orgPoints);

  const getTransactionIcon = (transaction: PointTransaction) => {
    if (transaction.missionTitle) return <Award size={20} />;
    if (transaction.reason.toLowerCase().includes('department')) return <User size={20} />;
    if (transaction.reason.toLowerCase().includes('profile')) return <CheckCircle size={20} />;
    if (transaction.reason.toLowerCase().includes('welcome')) return <Gift size={20} />;
    return transaction.points > 0 ? <TrendingUp size={20} /> : <TrendingDown size={20} />;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);

    if (diffInHours < 24) {
      return `Today at ${date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
    } else if (diffInHours < 48) {
      return `Yesterday at ${date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}`;
    } else if (diffInHours < 168) { // 7 days
      return date.toLocaleDateString('en-US', { weekday: 'short', hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    }
  };

  if (loading) {
    return (
      <div className={styles.pageContainer}>
        <div className={styles.loadingState}>Loading your ORG Points history...</div>
      </div>
    );
  }

  return (
    <div className={styles.pageContainer}>
      {/* Header Stats Section */}
      <div className={styles.headerSection}>
        <div className={styles.pageHeader}>
          <Coins size={32} className={styles.headerIcon} />
          <div>
            <h1 className={styles.pageTitle}>ORG Points History</h1>
            <p className={styles.pageSubtitle}>Track your earnings and spending throughout your TFIC journey</p>
          </div>
        </div>

        <div className={styles.statsGrid}>
          {/* Current Balance */}
          <div className={styles.statCard + ' ' + styles.balanceCard}>
            <div className={styles.statLabel}>Current Balance</div>
            <div className={styles.statValue}>{orgPoints.toLocaleString()}</div>
            <div className={styles.levelBadge}>
              Level {level} • {Math.round(progressPct)}% to Level {level + 1}
            </div>
            <div className={styles.progressBar}>
              <div className={styles.progressFill} style={{ width: `${progressPct}%` }}></div>
            </div>
          </div>

          {/* Total Earned */}
          <div className={styles.statCard + ' ' + styles.earnedCard}>
            <TrendingUp size={24} className={styles.statIcon} />
            <div className={styles.statLabel}>Total Earned</div>
            <div className={styles.statValue}>+{totalEarned.toLocaleString()}</div>
          </div>

          {/* Total Spent */}
          <div className={styles.statCard + ' ' + styles.spentCard}>
            <TrendingDown size={24} className={styles.statIcon} />
            <div className={styles.statLabel}>Total Spent</div>
            <div className={styles.statValue}>-{totalSpent.toLocaleString()}</div>
          </div>

          {/* Transaction Count */}
          <div className={styles.statCard}>
            <Calendar size={24} className={styles.statIcon} />
            <div className={styles.statLabel}>Transactions</div>
            <div className={styles.statValue}>{transactions.length}</div>
          </div>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className={styles.filterTabs}>
        <button 
          className={`${styles.filterTab} ${filter === 'all' ? styles.filterTabActive : ''}`}
          onClick={() => setFilter('all')}
        >
          All Transactions ({transactions.length})
        </button>
        <button 
          className={`${styles.filterTab} ${filter === 'earned' ? styles.filterTabActive : ''}`}
          onClick={() => setFilter('earned')}
        >
          Earned ({transactions.filter(t => t.points > 0).length})
        </button>
        <button 
          className={`${styles.filterTab} ${filter === 'spent' ? styles.filterTabActive : ''}`}
          onClick={() => setFilter('spent')}
        >
          Spent ({transactions.filter(t => t.points < 0).length})
        </button>
      </div>

      {/* Transaction Ledger */}
      <div className={styles.ledgerSection}>
        <h2 className={styles.sectionTitle}>Transaction Ledger</h2>
        
        {filteredTransactions.length === 0 ? (
          <div className={styles.emptyState}>
            <Coins size={48} />
            <h3>No transactions yet</h3>
            <p>Start earning ORG Points by completing missions, joining departments, and participating in events!</p>
          </div>
        ) : (
          <div className={styles.transactionList}>
            {filteredTransactions.map((transaction) => (
              <div 
                key={transaction.id} 
                className={`${styles.transactionItem} ${transaction.points > 0 ? styles.transactionEarned : styles.transactionSpent}`}
              >
                <div className={styles.transactionIcon}>
                  {getTransactionIcon(transaction)}
                </div>
                
                <div className={styles.transactionContent}>
                  <div className={styles.transactionMain}>
                    <span className={styles.transactionReason}>{transaction.reason}</span>
                    {transaction.missionTitle && (
                      <span className={styles.transactionMission}>Mission: {transaction.missionTitle}</span>
                    )}
                  </div>
                  
                  <div className={styles.transactionMeta}>
                    <span className={styles.transactionDate}>
                      <Calendar size={14} />
                      {formatDate(transaction.createdAt)}
                    </span>
                    {transaction.createdBy && (
                      <span className={styles.transactionCreator}>
                        <User size={14} />
                        Awarded by {transaction.createdBy}
                      </span>
                    )}
                  </div>
                </div>

                <div className={`${styles.transactionPoints} ${transaction.points > 0 ? styles.pointsPositive : styles.pointsNegative}`}>
                  {transaction.points > 0 ? '+' : ''}{transaction.points.toLocaleString()}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer Info */}
      <div className={styles.footerInfo}>
        <div className={styles.infoCard}>
          <h3>How to Earn ORG Points</h3>
          <ul>
            <li><strong>Complete Missions</strong> - Earn points for each completed mission (50+ points, 100+ for medical missions)</li>
            <li><strong>Join Departments</strong> - Get 25 points when approved for a department position</li>
            <li><strong>Complete Your Profile</strong> - Earn 15 points one-time for filling out your profile</li>
            <li><strong>Join the Organization</strong> - Receive 5 welcome points when your membership is approved</li>
            <li><strong>Attend Events</strong> - Participate in org events to earn additional rewards</li>
          </ul>
          <button 
            className={styles.infoButton}
            onClick={() => window.location.href = '/general/org-points'}
          >
            View ORG Points & Levels Guide →
          </button>
        </div>
      </div>
    </div>
  );
};

export default OrgPointsHistory;
